/**
 * Pal Stars TCG Price Index — a benchmark level for the Palworld OFFICIAL CARD
 * GAME market, not a per-card price.
 *
 * Where /prices answers "what is this card worth" and /price-dashboard answers
 * "what moved this week", this module answers "is the market up or down", by
 * turning the same daily price board into an equal-weight, base-1000 index plus
 * set and rarity sub-indices.
 *
 * Equal weight (one copy of each constituent, each contributing its own
 * percentage move) is deliberate: a market-cap weighting would let the single
 * four-figure SSS card dictate the whole index, so a benchmark built for
 * citation uses the average move of its constituents instead.
 *
 * Pure and dependency-free so the page, the CI checks and any future export can
 * share exactly one definition of the index.
 */
import type { PriceRow } from "./prices-shared";
import { RARITY_ORDER, SET_NAMES, type OfficialRarity } from "./tcg-cards";

/** Every index is published on the same base so levels are comparable. */
export const INDEX_BASE = 1000;

export interface IndexPoint {
  day: string;
  level: number;
}

export interface IndexSeries {
  id: string;
  label: string;
  /** One-line description of what the constituents are. */
  scope: string;
  constituents: number;
  points: IndexPoint[];
  level: number;
  change1d: number;
  change7d: number;
  change30d: number;
  /** Constituents up / flat / down over the last 7 days. */
  advancing: number;
  declining: number;
  unchanged: number;
  /** Combined value of one copy of every constituent, in USD. */
  basket: number;
  /** Market cap = total tracked value of the basket, per day. */
  capPoints: IndexPoint[];
  /** Latest market cap, in USD. */
  marketCap: number;
  /** Market cap move over the last 7 days / full window, in %. */
  capChange7d: number;
  capChange30d: number;
  /** Share of constituents whose latest point came from a stored marketplace quote. */
  liveCoverage: number;
  /** Calendar days between the first and last published point. */
  spanDays: number;
  /**
   * True when the series is long enough for an honest 7-day comparison. A
   * marketplace feed that only started a few days ago cannot publish a weekly
   * move, so the whole-life move must never be printed as one.
   */
  hasWeek: boolean;
}

/** Median of a list, used so a handful of penny printings cannot swing a level. */
function median(values: number[]): number {
  if (!values.length) return 0;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

const pct = (from: number, to: number) => (from > 0 ? Math.round(((to - from) / from) * 1000) / 10 : 0);
const round2 = (n: number) => Math.round(n * 100) / 100;

/** Level of the series roughly `back` calendar days before its last point. */
function levelDaysBack(points: IndexPoint[], back: number): number {
  const last = points[points.length - 1];
  if (!last) return 0;
  const target = Date.parse(`${last.day}T00:00:00Z`) - back * 86_400_000;
  for (let i = points.length - 1; i >= 0; i--) {
    if (Date.parse(`${points[i].day}T00:00:00Z`) <= target) return points[i].level;
  }
  return points[0].level;
}

/**
 * Build one base-1000 index from a set of rows.
 *
 * Every constituent is rebased on the same first day with broad coverage, so
 * the index level measures one fixed basket instead of changing membership.
 *
 * Constituents are aligned by CALENDAR DAY, never by array position: the
 * per-marketplace histories (eBay-only, TCGplayer-only) are ragged — each card
 * starts on the day that feed first quoted it and can skip days — so averaging
 * by position would mix values from different dates into one point. Days with
 * too few constituents are dropped, and the 1d/7d/30d moves are read from the
 * dated series rather than counted backwards through the array.
 */
export function buildIndex(
  id: string,
  label: string,
  scope: string,
  rows: PriceRow[],
  /** "equal" = average relative move; "cap" = total-value (market cap) weighted. */
  weight: "equal" | "cap" = "equal",
): IndexSeries | null {
  const candidates = rows.filter((r) => r.history.length > 1 && r.history.some((p) => p.value > 0));
  if (candidates.length === 0) return null;

  const coverage = new Map<string, number>();
  for (const row of candidates) {
    for (const point of row.history) {
      if (point.value > 0) coverage.set(point.day, (coverage.get(point.day) ?? 0) + 1);
    }
  }
  const minimumCoverage = Math.max(1, Math.ceil(candidates.length * 0.5));
  const baseDay = [...coverage.entries()]
    .filter(([, count]) => count >= minimumCoverage)
    .map(([day]) => day)
    .sort()[0];
  if (!baseDay) return null;

  // Freeze the basket on the first day with broad coverage. Every constituent
  // therefore uses the same dated baseline; late-arriving cards cannot join at
  // 1,000 halfway through the line and silently change the index composition.
  const usable = candidates.filter((row) => row.history.some((point) => point.day === baseDay && point.value > 0));
  if (usable.length === 0) return null;
  const baseByCode = new Map(
    usable.map((row) => [row.code, row.history.find((point) => point.day === baseDay)?.value ?? 0]),
  );
  const days = [...new Set(usable.flatMap((row) => row.history.map((point) => point.day).filter((day) => day >= baseDay)))].sort();
  if (days.length === 0) return null;

  // Carry the most recent validated quote forward across an occasional missed
  // sync. This keeps the fixed basket comparable instead of treating a missing
  // marketplace observation as a zero-dollar card or removing it for one day.
  const byDay = new Map<string, { rel: number[]; n: number; total: number }>();
  const latestByCode = new Map<string, number>();
  for (const day of days) {
    const bucket = { rel: [] as number[], n: 0, total: 0 };
    for (const row of usable) {
      const observed = row.history.find((point) => point.day === day && point.value > 0)?.value;
      if (observed !== undefined) latestByCode.set(row.code, observed);
      const value = latestByCode.get(row.code);
      const base = baseByCode.get(row.code) ?? 0;
      if (!(value && base)) continue;
      bucket.rel.push(value / base);
      bucket.n += 1;
      bucket.total += value;
    }
    byDay.set(day, bucket);
  }

  // Total tracked value per day — the market cap curve, shared by both weightings.
  const capPoints: IndexPoint[] = days.map((day) => ({
    day,
    level: Math.round((byDay.get(day)!.total || 0) * 100) / 100,
  }));
  const capBase = capPoints[0]?.level || 0;

  const points: IndexPoint[] =
    weight === "cap"
      ? capPoints.map((p) => ({
          day: p.day,
          level: capBase ? Math.round((p.level / capBase) * INDEX_BASE * 100) / 100 : INDEX_BASE,
        }))
      : days.map((day) => {
          // MEDIAN relative move, not the mean: a sub-dollar printing quoted at
          // $0.10 one day and $1.64 the next is a 16x relative move that would
          // drag a mean-based level hundreds of percent away from what the
          // typical card did. The median tracks the typical constituent.
          const b = byDay.get(day)!;
          return { day, level: b.rel.length ? Math.round(median(b.rel) * INDEX_BASE * 100) / 100 : INDEX_BASE };
        });

  const last = points.length - 1;
  const spanDays = Math.max(
    0,
    Math.round(
      (Date.parse(`${days[days.length - 1]}T00:00:00Z`) - Date.parse(`${days[0]}T00:00:00Z`)) / 86_400_000,
    ),
  );
  const hasWeek = spanDays >= 7;

  // Breadth counts the constituents' own 7-day moves. Per-marketplace series
  // report 0 until they hold a week of history, so breadth is only meaningful
  // once the series itself spans a week.
  let advancing = 0;
  let declining = 0;
  let unchanged = 0;
  for (const r of usable) {
    if (r.change7d > 0.05) advancing += 1;
    else if (r.change7d < -0.05) declining += 1;
    else unchanged += 1;
  }

  return {
    spanDays,
    hasWeek,
    id,
    label,
    scope,
    constituents: usable.length,
    points,
    level: points[last].level,
    change1d: pct(levelDaysBack(points, 1), points[last].level),
    // Never pass a 4-day-old feed's whole-life move off as a weekly move.
    change7d: hasWeek ? pct(levelDaysBack(points, 7), points[last].level) : 0,
    change30d: pct(points[0].level, points[last].level),
    advancing,
    declining,
    unchanged,
    basket: round2(usable.reduce((a, r) => a + r.market, 0)),
    capPoints,
    marketCap: capPoints[last]?.level ?? 0,
    capChange7d: pct(levelDaysBack(capPoints, 7), capPoints[last]?.level ?? 0),
    capChange30d: pct(capPoints[0]?.level ?? 0, capPoints[last]?.level ?? 0),
    liveCoverage: Math.round((usable.filter((r) => r.recordedDays > 0).length / usable.length) * 1000) / 10,
  };
}


/**
 * Japanese-printing index, day-aligned.
 *
 * Japanese quotes are stored under their own source and only start on the day
 * the Japanese feed first ran, so this series is keyed by calendar day rather
 * than by array position and only publishes days with at least three
 * constituents. Returns null until enough Japanese history exists.
 */
export function buildJapaneseIndex(rows: PriceRow[]): IndexSeries | null {
  const usable = rows.filter((r) => r.jpHistory.length > 0);
  if (usable.length < 3) return null;

  const first = new Map<string, number>();
  const byDay = new Map<string, { rel: number[]; total: number }>();
  for (const r of usable) {
    const base = r.jpHistory[0]?.value ?? 0;
    if (!base) continue;
    first.set(r.code, base);
    for (const p of r.jpHistory) {
      const bucket = byDay.get(p.day) ?? { rel: [], total: 0 };
      bucket.rel.push(p.value / base);
      bucket.total += p.value;
      byDay.set(p.day, bucket);
    }
  }
  const days = [...byDay.keys()].filter((d) => (byDay.get(d)?.rel.length ?? 0) >= 3).sort();
  if (days.length < 1) return null;

  const points: IndexPoint[] = days.map((day) => {
    const b = byDay.get(day)!;
    return { day, level: Math.round(median(b.rel) * INDEX_BASE * 100) / 100 };
  });
  const capPoints: IndexPoint[] = days.map((day) => ({ day, level: round2(byDay.get(day)!.total) }));
  const last = points.length - 1;
  const at = (back: number) => points[Math.max(0, last - back)].level;
  const spanDays = Math.max(
    0,
    Math.round(
      (Date.parse(`${days[days.length - 1]}T00:00:00Z`) - Date.parse(`${days[0]}T00:00:00Z`)) / 86_400_000,
    ),
  );

  return {
    spanDays,
    hasWeek: spanDays >= 7,
    id: "japan",
    label: "Japanese Printings",
    scope: `Separate benchmark for ${usable.length} Japanese printings, quoted in USD and never blended into the English index.`,
    constituents: usable.length,
    points,
    level: points[last].level,
    change1d: pct(at(1), points[last].level),
    change7d: pct(at(7), points[last].level),
    change30d: pct(points[0].level, points[last].level),
    advancing: 0,
    declining: 0,
    unchanged: usable.length,
    basket: round2(usable.reduce((a, r) => a + (r.jpAvg ?? 0), 0)),
    capPoints,
    marketCap: capPoints[last]?.level ?? 0,
    capChange7d: pct(capPoints[Math.max(0, last - 7)]?.level ?? 0, capPoints[last]?.level ?? 0),
    capChange30d: pct(capPoints[0]?.level ?? 0, capPoints[last]?.level ?? 0),
    liveCoverage: 100,
  };
}

/**
 * Median Japanese-versus-English premium across cards tracked in both markets,
 * in % (positive = the Japanese copy asks more). Null when coverage is too thin.
 */
export function japanesePremium(rows: PriceRow[]): { pct: number; cards: number } | null {
  const ratios = rows
    .filter((r) => r.jpAvg !== null && r.jpAvg > 0 && r.market > 0)
    .map((r) => (r.jpAvg as number) / r.market)
    .sort((a, b) => a - b);
  if (ratios.length < 3) return null;
  const mid = ratios[Math.floor(ratios.length / 2)];
  return { pct: Math.round((mid - 1) * 1000) / 10, cards: ratios.length };
}

/** Rarity buckets the index reports separately, coarsest first. */
export const RARITY_TIERS: Array<{ id: string; label: string; scope: string; rarities: OfficialRarity[] }> = [
  {
    id: "core",
    label: "Core Singles",
    scope: "Commons, Uncommons, Rares and Trial Deck cards — the cards you actually build decks from.",
    rarities: ["C", "U", "R", "TD"],
  },
  {
    id: "chase",
    label: "Chase Rares",
    scope: "Double Rares, Super Rares and Trial Deck Super Rares — the pack-pull hits.",
    rarities: ["RR", "SR", "TSR"],
  },
  {
    id: "parallel",
    label: "Parallels & Promos",
    scope: "SP, OSR, SSP, TSP, SSS and PR printings — the thin, collector-driven end of the market.",
    rarities: ["SP", "OSR", "SSP", "TSP", "SSS", "PR"],
  },
];

export interface IndexBoard {
  composite: IndexSeries | null;
  /** Same constituents, weighted by total tracked value instead of equally. */
  capWeighted: IndexSeries | null;
  /** Optional Japanese-printing benchmark, null until enough JP history exists. */
  japan: IndexSeries | null;
  /** Median Japanese premium over the English printing. */
  jpPremium: { pct: number; cards: number } | null;
  sets: IndexSeries[];
  tiers: IndexSeries[];
  /** Biggest 7-day contributors to the composite move. */
  leaders: PriceRow[];
  laggards: PriceRow[];
}

/**
 * Build the full published board: the composite index, one index per printed
 * set, one per rarity tier, and the constituents that moved the composite most.
 */
export function buildIndexBoard(rows: PriceRow[]): IndexBoard {
  const priced = rows.filter((r) => r.market > 0);

  const composite = buildIndex(
    "composite",
    "Pal Stars TCG Composite",
    `Equal-weight benchmark across all ${priced.length} tracked Palworld card printings.`,
    priced,
  );

  const capWeighted = buildIndex(
    "market-cap",
    "Market Cap Weighted",
    `Same ${priced.length} printings, weighted by total tracked market value — big-ticket chase cards move this line most.`,
    priced,
    "cap",
  );

  const setIds = [...new Set(priced.map((r) => r.set))].sort();
  const sets = setIds
    .map((set) =>
      buildIndex(
        `set-${set.toLowerCase()}`,
        SET_NAMES[set] ?? set,
        `Every printing in ${SET_NAMES[set] ?? set}.`,
        priced.filter((r) => r.set === set),
      ),
    )
    .filter((s): s is IndexSeries => s !== null);

  const tiers = RARITY_TIERS.map((t) =>
    buildIndex(
      `tier-${t.id}`,
      t.label,
      t.scope,
      priced.filter((r) => t.rarities.includes(r.rarity)),
    ),
  ).filter((s): s is IndexSeries => s !== null);

  const ranked = [...priced].sort((a, b) => b.change7d - a.change7d);
  return {
    composite,
    capWeighted,
    japan: buildJapaneseIndex(priced),
    jpPremium: japanesePremium(priced),
    sets,
    tiers,
    leaders: ranked.filter((r) => r.change7d > 0).slice(0, 8),
    laggards: ranked.filter((r) => r.change7d < 0).slice(-8).reverse(),
  };
}

/** Rarities in published order, used for the methodology table. */
export const INDEX_RARITY_ORDER = RARITY_ORDER;

export function formatLevel(n: number): string {
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatChange(n: number): string {
  return `${n > 0 ? "+" : ""}${n.toFixed(1)}%`;
}
